import axios from 'axios';
import { Subject } from 'rxjs';
import TokenService from '../services/TokenService';
import { toast } from 'react-toastify';
import React from 'react';
import KskToast  from 'ksk/styledComponents/components/KskToast/KskToast';
import { blobToStringSync } from './stringUtils';
import { HTTP_STATUS } from './HttpStatus';

const previewToken = (token) => {
  if (!token) {
    return '';
  }

  const sanitizedToken = String(token).trim();
  if (sanitizedToken.length <= 24) {
    return sanitizedToken;
  }

  return `${sanitizedToken.slice(0, 18)}...${sanitizedToken.slice(-6)}`;
};

const shouldLogAuthRequest = (url = '') => {
  return ['/auth/', '/acesso-stn/'].some((path) => url.includes(path));
};

const authGovBrLog = (step, payload) => {
  console.log(`[auth gov.br] ${step}`, payload ?? '');
};

const http = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_PATH}`,
  
  headers: {
    'Content-type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
});

export const isAxiosError = (error) => error?.isAxiosError === true;

export const handleUnhandledAxiosError = (event) => {
  if (isAxiosError(event.reason)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
};

window.addEventListener('unhandledrejection', handleUnhandledAxiosError, true);

let pendingRequests = 0;
const pendingRequests$ = new Subject(0); 

const incrementPendingRequests = () => {
  pendingRequests++;
  pendingRequests$.next(pendingRequests);
  //console.log('Pending Requests = ' + pendingRequests);
}

const decrementPendingRequests = () => {
  pendingRequests--;
  pendingRequests$.next(pendingRequests);
  //console.log('Pending Requests = ' + pendingRequests);
}

//const pipe = (...handlers) => arg => handlers.reduce((output, actual) => actual ? actual(output) : output, arg);
const pipeRequestHandlers = (...handlers) => arg => handlers.reduce((output, actual) => actual?.request ? actual.request(output) : output, arg);
const pipeResponseHandlers = (...handlers) => arg => handlers.reduce((output, actual) => actual?.response ? actual.response(output) : output, arg);

const autorizationHandler = {
  request: (config) => {
    if (!config.headers.Authorization) {
      const token = TokenService?.getAuthData()?.jwt ?? '';
      if (token) {
        config.headers.Authorization = token;
      }
    }
    return config;
  },

  response: (response) => {
    const newAuthorization = response.headers.authorization;
    //console.log('token response', newAuthorization);
    if (newAuthorization) {
      TokenService.setAuthData({jwt: newAuthorization});
    } 
    return response;
  }
}

const responseErrorHandler = (error) => {
  if (error?.response?.config?.responseType === 'blob') {
    error.response.data = JSON.parse(blobToStringSync(error?.response?.data));
  } 

  toast.error(<KskToast error httpError={error} />);
  return error;
};

const responseWarningHandler = (error) => {
  if (error?.response?.config?.responseType === 'blob') {
    error.response.data = JSON.parse(blobToStringSync(error?.response?.data));
  }

  toast.warn(<KskToast warning httpError={error} />);
  return error;
};

const responseSuccessWarningHandler = (response) => {
  const warnMessage = response?.data?.warnMessage;

  if (warnMessage) {
    toast.warn(<KskToast warning mainMessage={warnMessage} />);
  }

  return response;
};

http.interceptors.request.use(
  (config) => { 
    incrementPendingRequests();
    if (shouldLogAuthRequest(config?.url)) {
      authGovBrLog('HTTP request', {
        method: config?.method,
        url: `${config?.baseURL ?? ''}${config?.url ?? ''}`,
        hasAuthorizationHeader: !!config?.headers?.Authorization,
        authorizationPreview: previewToken(config?.headers?.Authorization),
        params: config?.params,
        data: config?.data,
      });
    }
    return pipeRequestHandlers(autorizationHandler)(config);
  },
  (error) => {
    decrementPendingRequests();
    return Promise.reject(error);
  }
);

http.interceptors.response.use(
  (response) => {
    decrementPendingRequests();
    const handledResponse = pipeResponseHandlers(autorizationHandler)(response);
    if (shouldLogAuthRequest(response?.config?.url)) {
      authGovBrLog('HTTP response', {
        url: `${response?.config?.baseURL ?? ''}${response?.config?.url ?? ''}`,
        status: response?.status,
        authorizationHeaderPreview: previewToken(response?.headers?.authorization),
        data: response?.data,
      });
    }
    return responseSuccessWarningHandler(handledResponse);
  },
  (error) => {
    decrementPendingRequests();

    const status = error?.response?.status;

    if (shouldLogAuthRequest(error?.config?.url || error?.response?.config?.url)) {
      authGovBrLog('HTTP error', {
        url: `${error?.config?.baseURL ?? error?.response?.config?.baseURL ?? ''}${error?.config?.url ?? error?.response?.config?.url ?? ''}`,
        status,
        message: error?.message,
        data: error?.response?.data,
      });
    }

    if (status === 401) {
      TokenService.removeAuthData();
    }

    // TRATAMENTO GLOBAL DO 403
    if (status === 403) {
      responseErrorHandler(error);

      // NÃO rejeita → React 18 não quebra
      return Promise.resolve({
        data: null,
        status: 403,
        headers: {},
        config: error.config
      });
    }

    // TRATAMENTO GLOBAL DO WARNING (mensagem de advertência, mas impede a continuidade da operação como um erro)
    if (status === HTTP_STATUS.WARNING_470) {
      return Promise.reject(responseWarningHandler(error));
    }

    // Demais erros continuam sendo erros
    return Promise.reject(responseErrorHandler(error));
  }
);

export const watchPendingRequests$ = pendingRequests$.asObservable();
export default http;

/* const HANDLER_TEMPLATE = {
  request: (config) => {
    return config;
  },
  response: (response) => {
    return response;
  }
}; */
